// src/pages/mock-data-management-dashboard/components/ExportPanel.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ExportPanel = ({ activeTab, mockData, onExport }) => {
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const getExportFormats = () => {
    return [
      {
        format: 'json',
        label: 'JSON',
        description: 'JavaScript Object Notation',
        icon: 'FileCode',
        mimetype: 'application/json'
      },
      {
        format: 'csv',
        label: 'CSV',
        description: 'Comma Separated Values',
        icon: 'FileSpreadsheet',
        mimetype: 'text/csv'
      },
      {
        format: 'sql',
        label: 'SQL',
        description: 'SQL Insert Statements',
        icon: 'Database',
        mimetype: 'text/sql'
      }
    ];
  };

  const convertToJSON = (data) => {
    return JSON.stringify(data, null, 2);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    
    const csvRows = data.map(row => {
      return headers.map(header => {
        let value = row[header];
        
        // Handle complex types
        if (Array.isArray(value)) {
          value = value.join('; ');
        } else if (value instanceof Date) {
          value = value.toISOString();
        } else if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value);
        }
        
        // Escape quotes and wrap in quotes if contains comma or quote
        value = String(value || '');
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = '"' + value.replace(/"/g, '""') + '"';
        }
        
        return value;
      }).join(',');
    });
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  const convertToSQL = (data, tableName) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const sqlStatements = [];
    
    // Add table creation comment
    sqlStatements.push(`-- SQL Insert Statements for ${tableName}`);
    sqlStatements.push(`-- Generated on ${new Date().toISOString()}`);
    sqlStatements.push('');
    
    // Generate INSERT statements
    data.forEach(row => {
      const values = headers.map(header => {
        let value = row[header];
        
        if (value === null || value === undefined) {
          return 'NULL';
        } else if (typeof value === 'string') {
          return `'${value.replace(/'/g, "''")}'`;
        } else if (value instanceof Date) {
          return `'${value.toISOString()}'`;
        } else if (Array.isArray(value)) {
          return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
        } else if (typeof value === 'object') {
          return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
        } else {
          return value;
        }
      });
      
      sqlStatements.push(
        `INSERT INTO ${tableName} (${headers.join(', ')}) VALUES (${values.join(', ')});`
      );
    });
    
    return sqlStatements.join('\n');
  };

  const downloadFile = (content, filename, mimetype) => {
    const blob = new Blob([content], { type: mimetype });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    setShowExportDropdown(false);
    
    try {
      // Simulate export processing time
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const data = mockData[activeTab] || [];
      const timestamp = new Date().toISOString().split('T')[0];
      const entityName = activeTab.replace(/([A-Z])/g, '_$1').toLowerCase();
      
      let content = '';
      let filename = '';
      let mimetype = '';
      
      switch (format) {
        case 'json':
          content = convertToJSON(data);
          filename = `${entityName}_${timestamp}.json`;
          mimetype = 'application/json';
          break;
        case 'csv':
          content = convertToCSV(data);
          filename = `${entityName}_${timestamp}.csv`;
          mimetype = 'text/csv';
          break;
        case 'sql':
          content = convertToSQL(data, entityName);
          filename = `${entityName}_${timestamp}.sql`;
          mimetype = 'text/sql';
          break;
        default:
          throw new Error('Unsupported format');
      }
      
      downloadFile(content, filename, mimetype);
      onExport?.();
      
    } catch (error) {
      console.error('Export failed:', error);
      // You could show a toast notification here
    } finally {
      setIsExporting(false);
    }
  };

  const exportFormats = getExportFormats();
  const currentDataCount = mockData[activeTab]?.length || 0;

  return (
    <div className="relative">
      <button
        onClick={() => setShowExportDropdown(!showExportDropdown)}
        disabled={currentDataCount === 0 || isExporting}
        className="btn btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <>
            <Icon name="Loader2" size={16} className="animate-spin" />
            <span>Exporting...</span>
          </>
        ) : (
          <>
            <Icon name="Download" size={16} />
            <span>Export</span>
            <Icon name="ChevronDown" size={14} />
          </>
        )}
      </button>

      {/* Export Dropdown */}
      {showExportDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-surface rounded-lg shadow-xl border border-secondary-200 z-1001 animation-fade-in">
          <div className="p-4 border-b border-secondary-200">
            <h3 className="text-sm font-semibold text-text-primary mb-1">Export Data</h3>
            <p className="text-xs text-text-secondary">
              {currentDataCount} {activeTab} records
            </p>
          </div>
          
          <div className="py-2">
            {exportFormats.map((format) => (
              <button
                key={format.format}
                onClick={() => handleExport(format.format)}
                className="w-full flex items-center px-4 py-3 text-left hover:bg-secondary-50 transition-colors duration-150"
              >
                <div className="w-8 h-8 bg-secondary-100 rounded-lg flex items-center justify-center mr-3">
                  <Icon name={format.icon} size={16} className="text-secondary-600" />
                </div>
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    {format.label}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {format.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          <div className="p-3 border-t border-secondary-200 bg-secondary-50">
            <div className="text-xs text-text-secondary">
              <Icon name="Info" size={12} className="inline mr-1" />
              Files will be downloaded to your default download folder
            </div>
          </div>
        </div>
      )}

      {/* Click outside handler */}
      {showExportDropdown && (
        <div
          className="fixed inset-0 z-1000"
          onClick={() => setShowExportDropdown(false)}
        />
      )}
    </div>
  );
};

export default ExportPanel;